import React, { useEffect, useState, useRef } from "react";
import { getDatabase, ref, onValue, get, push, update } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { useGetCurrentUserId } from "../hooks/useGetCurrentUserId";
import {
  Eye,
  Download,
  ArrowLeft,
  Info,
  X,
  FileText,
  Truck,
  Package,
  ChevronDown,
  ChevronUp,
  MapPin,
  Phone,
  Upload,
  CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./UserProofOfDelivery.css";

const UserProofOfDelivery = () => {
  const { userId, loading: userLoading } = useGetCurrentUserId();
  const [orderDocuments, setOrderDocuments] = useState([]);
  const [fullOrders, setFullOrders] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
  const [selectedDocumentDetail, setSelectedDocumentDetail] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDoc, setPaymentDoc] = useState(null); // { orderId, transporterId, ... }
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [utrNumber, setUtrNumber] = useState("");
  const [paymentFile, setPaymentFile] = useState(null);
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentSuccess, setPaymentSuccess] = useState("");

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const navigate = useNavigate();
  const currentUserIdRef = useRef(userId);
  const db = getDatabase();
  const storage = getStorage();

  useEffect(() => {
    currentUserIdRef.current = userId;
    setOrderDocuments([]);
    setFullOrders({});
    setIsLoading(true);
    setExpandedOrders({});
  }, [userId]);

  useEffect(() => {
    if (userLoading || !userId) {
      setIsLoading(false);
      return;
    }

    const docsRef = ref(db, "transport_documents");
    const ordersRef = ref(db, "transport_orders");
    const profilesRef = ref(db, "transporter-profile");

    let unsubDocs;
    let unsubOrders;
    let profilesCache = {};

    const processDocuments = (allDocs, orderMap) => {
      if (currentUserIdRef.current !== userId) return;

      const processed = [];

      Object.keys(allDocs || {}).forEach((transporterId) => {
        const transporterData = allDocs[transporterId];
        if (!transporterData || typeof transporterData !== "object") return;

        const profile = profilesCache[transporterId] || {};

        Object.keys(transporterData).forEach((orderId) => {
          if (orderId === "POD" || orderId === "invoices" || orderId === "payments")
            return;

          const orderLevel = transporterData[orderId];
          if (!orderLevel || typeof orderLevel !== "object") return;

          const pods = [];
          const invoices = [];
          const payments = [];

          // PODs
          if (orderLevel.POD && typeof orderLevel.POD === "object") {
            Object.keys(orderLevel.POD).forEach((key) => {
              const pod = orderLevel.POD[key];
              if (!pod) return;
              const matchesClient =
                pod.clientId === userId ||
                (orderMap[orderId] && orderMap[orderId].clientId === userId);
              if (matchesClient) pods.push({ id: key, ...pod });
            });
          }

          // Invoices
          if (orderLevel.invoices && typeof orderLevel.invoices === "object") {
            Object.entries(orderLevel.invoices).forEach(([key, inv]) => {
              if (!inv) return;
              const matchesClient =
                inv.clientId === userId ||
                (orderMap[orderId] && orderMap[orderId].clientId === userId);
              if (matchesClient) invoices.push({ id: key, ...inv });
            });
          }

          // Payments (for display if needed later)
          if (orderLevel.payments && typeof orderLevel.payments === "object") {
            Object.entries(orderLevel.payments).forEach(([key, pay]) => {
              if (!pay) return;
              const matchesClient =
                pay.clientId === userId ||
                (orderMap[orderId] && orderMap[orderId].clientId === userId);
              if (matchesClient) payments.push({ id: key, ...pay });
            });
          }

          if (pods.length > 0 || invoices.length > 0 || payments.length > 0) {
            processed.push({
              orderId,
              transporterId,
              transporterName:
                profile.companyName ||
                profile.contactPerson ||
                "Unknown Transporter",
              transporterPhone: profile.phone || "N/A",
              pods: pods.sort(
                (a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0)
              ),
              invoices: invoices.sort(
                (a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0)
              ),
              payments: payments.sort(
                (a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0)
              ),
            });
          }
        });
      });

      processed.sort((a, b) => {
        const maxTime = (doc) => {
          const times = [
            ...(doc.pods?.map((p) => p.uploadedAt) || []),
            ...(doc.invoices?.map((i) => i.uploadedAt) || []),
            ...(doc.payments?.map((p) => p.uploadedAt) || []),
          ];
          return times.length ? Math.max(...times) : 0;
        };
        return maxTime(b) - maxTime(a);
      });

      setOrderDocuments(processed);
      setIsLoading(false);
    };

    const loadData = async () => {
      setIsLoading(true);
      try {
        const profileSnap = await get(profilesRef);
        profilesCache = profileSnap.val() || {};

        let liveOrderMap = {};

        unsubOrders = onValue(ordersRef, (snapshot) => {
          if (currentUserIdRef.current !== userId) return;
          const data = snapshot.val() || {};
          const orderMap = {};

          Object.values(data).forEach((transporterOrders) => {
            if (!transporterOrders || typeof transporterOrders !== "object")
              return;
            Object.values(transporterOrders).forEach((order) => {
              if (order && order.clientId === userId && order.orderId) {
                orderMap[order.orderId] = order;
              }
            });
          });

          liveOrderMap = orderMap;
          setFullOrders(orderMap);
        });

        unsubDocs = onValue(docsRef, (snapshot) => {
          if (currentUserIdRef.current !== userId) return;
          processDocuments(snapshot.val() || {}, liveOrderMap);
        });
      } catch (err) {
        console.error("Error loading documents:", err);
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      if (unsubDocs) unsubDocs();
      if (unsubOrders) unsubOrders();
    };
  }, [userId, userLoading]);

  const handleDownload = (url) => {
    if (url) window.open(url, "_blank");
  };

  const openOrderDetail = (orderId, doc) => {
    setSelectedOrderDetail(fullOrders[orderId] || null);
    setSelectedDocumentDetail(doc);
    setShowDetailPopup(true);
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  // ==================== PAYMENT ====================
  const openPaymentModal = (doc) => {
    setPaymentDoc(doc);
    setPaymentAmount("");
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setUtrNumber("");
    setPaymentFile(null);
    setPaymentError("");
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentDoc(null);
    setPaymentAmount("");
    setUtrNumber("");
    setPaymentFile(null);
    setPaymentError("");
  };

  const handlePaymentSubmit = async () => {
    if (!paymentAmount || Number(paymentAmount) <= 0) {
      return setPaymentError("Please enter a valid payment amount.");
    }
    if (!utrNumber.trim() && !paymentFile) {
      return setPaymentError("Please enter UTR number or upload payment proof (or both)."); 
    }
    // UTR validation
    if (utrNumber.trim()) { const utr = utrNumber.trim();
      // Only letters and numbers
      if (!/^[A-Z0-9]+$/.test(utr)) { 
        return setPaymentError( "UTR Number can contain only letters and numbers." );
       }
       // UTR should not exceed 22 characters
       if (utr.length > 22) {
         return setPaymentError( "UTR Number cannot be more than 22 characters." );
         }
         if (utr.length < 12) { return setPaymentError( "Please enter a valid UTR Number." );
          } 
        }
    if (paymentFile && paymentFile.size > MAX_FILE_SIZE) {
      return setPaymentError("File size must be less than 5 MB.");
    }

    setUploadingPayment(true);
    setPaymentError("");

    try {
      const { orderId, transporterId, transporterName } = paymentDoc;
      const order = fullOrders[orderId];
      const timestamp = Date.now();

      let fileUrl = null;
      let fileName = null;

      // Upload proof file if provided
      if (paymentFile) {
        const filePath = `transport_documents/${transporterId}/${orderId}/payments/${timestamp}_${paymentFile.name}`;
        const fileStorageRef = storageRef(storage, filePath);
        await uploadBytes(fileStorageRef, paymentFile);
        fileUrl = await getDownloadURL(fileStorageRef);
        fileName = paymentFile.name;
      }

      // Payment data
      const paymentData = {
        amount: Number(paymentAmount),
        paymentDate: paymentDate,
        utrNumber: utrNumber.trim() || null,
        fileUrl: fileUrl || null,
        uploadedAt: timestamp,
      };

      // Save under payments subnode (same pattern as invoices / POD)
      const paymentRef = push(
        ref(db, `transport_documents/${transporterId}/${orderId}/payments`)
      );
      await update(paymentRef, paymentData);

      // Also keep order-level metadata if needed
      await update(ref(db, `transport_documents/${transporterId}/${orderId}`), {
        orderId,
        clientId: userId,
        transporterId,
        from: order?.from || null,
        to: order?.to || null,
      });

      const routeInfo = order
        ? `${order.from} → ${order.to}`
        : orderId;

      // 1. supplier_notifications (for the transporter)
      const supplierNotifRef = push(
        ref(db, `supplier_notifications/Payment/${transporterId}`)
      );
      await update(supplierNotifRef, {
        category: "Payment",
        title: "Payment Received",
        message: `${order?.clientName || "Client"} has submitted a payment of ₹${Number(
          paymentAmount
        ).toLocaleString()} for Order #${orderId} (${routeInfo}).${utrNumber.trim() ? ` UTR: ${utrNumber.trim()}` : ""
          }`,
        gifUrl: "/Images/New notification.gif",
        seen: false,
        timestamp,
      });

      // Also add to transport_notifications under transporter (common pattern)
      const transporterNotifRef = push(
        ref(db, `transport_notifications/${transporterId}`)
      );
      const transporterNotifId = transporterNotifRef.key;
      await update(transporterNotifRef, {
        id: transporterNotifId,
        category: "Payment",
        pageTitle: "Payment",
        title: "Payment Received",
        message: `${order?.clientName || "Client"} has submitted a payment of ₹${Number(
          paymentAmount
        ).toLocaleString()} for Order #${orderId} (${routeInfo}).${utrNumber.trim() ? ` UTR: ${utrNumber.trim()}` : ""
          }`,
        isSupplier: true,
        read: false,
        createdAt: timestamp,
        timestamp,
      });

      setPaymentSuccess(
        `Payment of ₹${Number(paymentAmount).toLocaleString()} submitted successfully!`
      );
      setTimeout(() => setPaymentSuccess(""), 3000);
      closePaymentModal();
    } catch (err) {
      console.error("Payment submit error:", err);
      setPaymentError(err.message || "Failed to submit payment. Please try again.");
    } finally {
      setUploadingPayment(false);
    }
  };

  if (userLoading || isLoading) {
    return (
      <div className="upd-loading-wrap">
        <div className="upd-spinner" />
        <p className="upd-loading-text">Loading documents...</p>
      </div>
    );
  }

  return (
    <div className="upd-page">
      <div className="upd-container">
        {/* Header */}
        <div className="upd-header">
          <button className="upd-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
            <span className="upd-back-text">Back</span>
          </button>
          <h1 className="upd-title">Documents</h1>
          <button className="upd-info-btn" onClick={() => setShowInfo(true)}>
            <Info size={18} />
          </button>
        </div>

        {/* Success toast */}
        {paymentSuccess && (
          <div className="upd-success-toast">
            <CheckCircle size={18} />
            {paymentSuccess}
          </div>
        )}

        {/* Empty */}
        {orderDocuments.length === 0 ? (
          <div className="upd-empty">
            <FileText size={40} className="upd-empty-icon" />
            <p className="upd-empty-title">No documents yet</p>
            <p className="upd-empty-sub">
              PODs and invoices from your transporters will appear here
            </p>
          </div>
        ) : (
          <div className="upd-list">
            {orderDocuments.map((doc) => {
              const order = fullOrders[doc.orderId];
              const remaining =
                (order?.quantity || 0) -
                (order?.dispatchSummary?.totalLoad || 0);
              const isFullyDispatched = remaining <= 0;
              const isExpanded = expandedOrders[doc.orderId];
              const totalPods = doc.pods.length;
              const totalInvoices = doc.invoices.length;
              const totalPayments = doc.payments?.length || 0;

              return (
                <div
                  key={`${doc.transporterId}-${doc.orderId}`}
                  className="upd-card"
                >
                  {/* Summary */}
                  <div className="upd-card-summary">
                    <div className="upd-card-left">
                      <div className="upd-card-top-row">
                        <h2 className="upd-order-id">#{doc.orderId}</h2>
                        {order &&
                          (isFullyDispatched ? (
                            <span className="upd-badge upd-badge-green">
                              Fully Dispatched
                            </span>
                          ) : (
                            <span className="upd-badge upd-badge-amber">
                              Remaining: {remaining} {order?.quantityUnit}
                            </span>
                          ))}
                      </div>

                      <div className="upd-route">
                        <MapPin size={14} className="upd-route-icon" />
                        <span>
                          {order?.from || "—"} → {order?.to || "—"}
                        </span>
                      </div>

                      <div className="upd-meta">
                        {order?.quantity != null && (
                          <span className="upd-meta-item">
                            <Package size={12} />
                            {order.quantity} {order.quantityUnit || ""}
                          </span>
                        )}
                        <span className="upd-meta-item">
                          <Truck size={12} />
                          {doc.transporterName}
                        </span>
                        {doc.transporterPhone &&
                          doc.transporterPhone !== "N/A" && (
                            <span className="upd-meta-item">
                              <Phone size={12} />
                              {doc.transporterPhone}
                            </span>
                          )}
                      </div>
                    </div>

                    <div className="upd-card-actions">
                      <div className="upd-count-chips">
                        <span className="upd-chip upd-chip-blue">
                          <FileText size={12} />
                          {totalPods} POD{totalPods !== 1 ? "s" : ""}
                        </span>
                        <span className="upd-chip upd-chip-violet">
                          <FileText size={12} />
                          {totalInvoices} Invoice
                          {totalInvoices !== 1 ? "s" : ""}
                        </span>
                         <span className="upd-chip upd-chip-violet">
                          <FileText size={12} />
                          {totalPayments} Payment
                          {totalPayments !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <button
                        className="upd-icon-btn"
                        onClick={() => openOrderDetail(doc.orderId, doc)}
                        title="View full details"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        className="upd-icon-btn"
                        onClick={() => toggleExpand(doc.orderId)}
                        title={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded */}
                  {isExpanded && (
                    <div className="upd-expanded">
                      {/* PODs */}
                      <div className="upd-section">
                        <h3 className="upd-section-title">
                          <FileText size={13} />
                          Proof of Delivery ({totalPods})
                        </h3>
                        {totalPods === 0 ? (
                          <p className="upd-empty-line">
                            No POD uploaded yet for this order
                          </p>
                        ) : (
                          <div className="upd-file-list">
                            {doc.pods.map((pod) => (
                              <div key={pod.id} className="upd-file-row">
                                <div className="upd-file-grid">
                                  <div>
                                    <span className="upd-file-label">Truck</span>
                                    <span className="upd-file-value">
                                      {pod.truckNumber || "—"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="upd-file-label">Driver</span>
                                    <span className="upd-file-value">
                                      {pod.driverNumber || pod.driver || "—"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="upd-file-label">Load</span>
                                    <span className="upd-file-value">
                                      {pod.load ?? pod.quantity ?? "—"}
                                      {pod.quantityUnit
                                        ? ` ${pod.quantityUnit}`
                                        : ""}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="upd-file-label">
                                      Uploaded
                                    </span>
                                    <span className="upd-file-value upd-file-date">
                                      {pod.uploadedAt
                                        ? new Date(
                                          pod.uploadedAt
                                        ).toLocaleString()
                                        : "—"}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  className="upd-download-btn upd-download-blue"
                                  onClick={() => handleDownload(pod.fileUrl)}
                                >
                                   <Eye size={13} />
  <span>POD</span>
  <Download size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Invoices */}
                      <div className="upd-section">
                        <h3 className="upd-section-title">
                          <FileText size={13} />
                          Invoices ({totalInvoices})
                        </h3>
                        {totalInvoices === 0 ? (
                          <p className="upd-empty-line">
                            No invoices uploaded yet
                          </p>
                        ) : (
                          <div className="upd-file-list">
                            {doc.invoices.map((inv) => (
                              <div key={inv.id} className="upd-file-row">
                                <div className="upd-file-grid">
                                  <div>
                                    <span className="upd-file-label">
                                      Bill No.
                                    </span>
                                    <span className="upd-file-value">
                                      {inv.billNumber || "—"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="upd-file-label">
                                      Amount
                                    </span>
                                    <span className="upd-file-value">
                                      {inv.amount
                                        ? `₹${Number(
                                          inv.amount
                                        ).toLocaleString()}`
                                        : "—"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="upd-file-label">
                                      Uploaded
                                    </span>
                                    <span className="upd-file-value upd-file-date">
                                      {inv.uploadedAt
                                        ? new Date(
                                          inv.uploadedAt
                                        ).toLocaleString()
                                        : "—"}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  className="upd-download-btn upd-download-violet"
                                  onClick={() => handleDownload(inv.fileUrl)}
                                >
                                  <Eye size={13} />
                                   Invoice
                                  <Download size={13} />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Submit Payment Button */}
                        {totalInvoices > 0 && (
                          <button
                            className="upd-payment-btn"
                            onClick={() => openPaymentModal(doc)}
                          >
                            Update Payment
                          </button>
                        )}
                      </div>

                      {/* Existing Payments (optional display) */}
                      {totalPayments > 0 && (
                        <div className="upd-section upd-section-last">
                          <h3 className="upd-section-title">
                            <FileText size={13} />
                            Payments Submitted ({totalPayments})
                          </h3>
                          <div className="upd-file-list">
                            {doc.payments.map((pay) => (
                              <div key={pay.id} className="upd-file-row">
                                <div className="upd-file-grid">
                                  <div>
                                    <span className="upd-file-label">
                                      Amount
                                    </span>
                                    <span className="upd-file-value">
                                      ₹{Number(pay.amount || 0).toLocaleString()}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="upd-file-label">UTR</span>
                                    <span className="upd-file-value">
                                      {pay.utrNumber || "—"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="upd-file-label">Date</span>
                                    <span className="upd-file-value">
                                      {pay.paymentDate || "—"}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="upd-file-label">
                                      Submitted
                                    </span>
                                    <span className="upd-file-value upd-file-date">
                                      {pay.uploadedAt
                                        ? new Date(
                                          pay.uploadedAt
                                        ).toLocaleString()
                                        : "—"}
                                    </span>
                                  </div>
                                </div>
                               <div className="upd-payment-action"> {pay.fileUrl && ( <button className="upd-download-btn upd-download-blue" onClick={() => handleDownload(pay.fileUrl)} >  <Eye size={13} /> Payment <Download size={13} /> </button> )} </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ==================== PAYMENT MODAL ==================== */}
      {showPaymentModal && paymentDoc && (
        <div className="upd-overlay">
          <div className="upd-payment-modal">
            <div className="upd-payment-modal-header">
              <h3 className="upd-payment-modal-title">Update Payment</h3>
              <button className="upd-modal-close" onClick={closePaymentModal}>
                <X size={20} />
              </button>
            </div>

            <div className="upd-payment-modal-body">
              <p className="upd-payment-order">
                Order <strong>#{paymentDoc.orderId}</strong> ·{" "}
                {paymentDoc.transporterName}
              </p>

              {/* Amount */}
              <div className="upd-form-group">
                <label className="upd-form-label">Payment Amount (₹) *</label>
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="upd-form-input"
                />
              </div>

              {/* Date (auto today) */}
              <div className="upd-form-group">
                <label className="upd-form-label">Payment Date</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="upd-form-input"
                />
              </div>

              {/* UTR */}
              <div className="upd-form-group">
                <label className="upd-form-label">
                  UTR Number{" "}
                  <span className="upd-optional">(optional if proof uploaded)</span>
                </label>
                <input type="text" placeholder="Enter UTR / Transaction ID" value={utrNumber} maxLength={22} onChange={(e) => { const value = e.target.value .replace(/[^a-zA-Z0-9]/g, "") .toUpperCase(); setUtrNumber(value); setPaymentError(""); }} className="upd-form-input" /> <span className="upd-input-hint"> {utrNumber.length}/22 </span>
              </div>

              {/* File Upload */}
              <div className="upd-form-group">
                <label className="upd-form-label">
                  Payment Proof{" "}
                  <span className="upd-optional">(optional if UTR entered)</span>
                </label>
                <label className="upd-file-picker">
                  <div className="upd-file-picker-inner">
                    <Upload size={18} />
                    <div>
                      <p className="upd-file-picker-name">
                        {paymentFile
                          ? paymentFile.name
                          : "Select payment proof"}
                      </p>
                      <p className="upd-file-picker-hint">
                        PDF, JPG, PNG · Max 5 MB
                      </p>
                    </div>
                  </div>
                  <span className="upd-file-picker-browse">Browse</span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    className="upd-hidden-input"
                    onChange={(e) =>
                      setPaymentFile(e.target.files[0] || null)
                    }
                  />
                </label>
              </div>

              <p className="upd-form-hint">
                You can submit with UTR only, proof file only, or both.
              </p>

              {paymentError && (
                <p className="upd-form-error">{paymentError}</p>
              )}
            </div>

            <div className="upd-payment-modal-footer">
              <button
                className="upd-btn-cancel"
                onClick={closePaymentModal}
                disabled={uploadingPayment}
              >
                Cancel
              </button>
              <button
                className="upd-btn-submit"
                onClick={handlePaymentSubmit}
                disabled={uploadingPayment}
              >
                {uploadingPayment ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {showInfo && (
        <div className="upd-overlay">
          <div className="upd-modal">
            <div className="upd-modal-header">
              <h3 className="upd-modal-title">About Documents</h3>
              <button
                className="upd-modal-close"
                onClick={() => setShowInfo(false)}
              >
                <X size={20} />
              </button>
            </div>
           <div className="upd-modal-text">
  <p>
    All Proof of Delivery (POD) documents and invoices uploaded by
    your transporters are shown here.
  </p>

  <p>
    <strong>How to use this section:</strong>
  </p>

  <ul className="upd-info-list">
    <li>Expand an order to view or download its POD and invoice.</li>
    <li>
      Once an invoice is available, click <strong>Submit Payment</strong>{" "}
      to submit your payment directly from here.
    </li>
    <li>Enter the payment amount and payment date.</li>
    <li>
      Provide the <strong>UTR/Transaction ID</strong> or upload your{" "}
      <strong>Payment Proof</strong>. You can also provide both.
    </li>
    <li>
      After submission, your payment details will be shared with the
      transporter for verification.
    </li>
  </ul>
</div>
          </div>
        </div>
      )}

      {/* Detail Popup – keep your existing one */}
      {showDetailPopup && (
        <div className="upd-overlay">
          <div className="upd-detail-modal">
            {/* ... keep your existing detail modal content ... */}
            <div className="upd-detail-header">
              <h3 className="upd-detail-title">Order Details</h3>
              <button
                className="upd-modal-close"
                onClick={() => setShowDetailPopup(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="upd-detail-body">
              {selectedOrderDetail ? (
                <div className="upd-detail-grid">
                  <div className="upd-detail-item">
                    <span className="upd-detail-label">Order ID</span>
                    <strong>{selectedOrderDetail.orderId}</strong>
                  </div>
                  <div className="upd-detail-item">
                    <span className="upd-detail-label">Status</span>
                    <strong>{selectedOrderDetail.status || "—"}</strong>
                  </div>
                  <div className="upd-detail-item">
                    <span className="upd-detail-label">Total Quantity</span>
                    <strong>
                      {selectedOrderDetail.quantity}{" "}
                      {selectedOrderDetail.quantityUnit}
                    </strong>
                  </div>
                  <div className="upd-detail-item">
                    <span className="upd-detail-label">Remaining</span>
                    <strong>
                      {(selectedOrderDetail.quantity || 0) -
                        (selectedOrderDetail.dispatchSummary?.totalLoad ||
                          0)}{" "}
                      {selectedOrderDetail.quantityUnit}
                    </strong>
                  </div>
                  <div className="upd-detail-item">
                    <span className="upd-detail-label">From</span>
                    <strong>{selectedOrderDetail.from || "—"}</strong>
                  </div>
                  <div className="upd-detail-item">
                    <span className="upd-detail-label">To</span>
                    <strong>{selectedOrderDetail.to || "—"}</strong>
                  </div>
                  <div className="upd-detail-item">
                    <span className="upd-detail-label">Transporter</span>
                    <strong>
                      {selectedDocumentDetail?.transporterName || "—"}
                    </strong>
                  </div>
                  <div className="upd-detail-item">
                    <span className="upd-detail-label">Phone</span>
                    <strong>
                      {selectedDocumentDetail?.transporterPhone || "—"}
                    </strong>
                  </div>
                </div>
              ) : (
                <p className="upd-empty-line">Order details not found.</p>
              )}
            </div>
            <div className="upd-detail-footer">
              <button
                className="upd-close-btn"
                onClick={() => setShowDetailPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProofOfDelivery;